import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Marquee from '../components/Marquee';
import HowItWorks from '../components/HowItWorks';
import PredictionCarousel from '../components/PredictionCarousel';
import AuthModal from '../components/AuthModal';
import { setMetaTags, resetMetaTags, buildCanonicalUrl, buildWebsiteJsonLd, PAGE_SEO } from '../utils/seo';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('register');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // SEO meta-теги для главной страницы
  useEffect(() => {
    setMetaTags({
      ...PAGE_SEO.home,
      canonicalUrl: buildCanonicalUrl('/'),
      jsonLd: buildWebsiteJsonLd(),
    });

    return () => {
      resetMetaTags();
    };
  }, []);

  return (
    <div style={{
      position: 'relative',
      minHeight: '100vh',
    }}>
      {/* Бегущая строка с астрологическим календарём */}
      <Marquee />

      {/* Блок описания платформы */}
      <div style={{
        padding: isMobile ? '32px 16px' : '40px 20px',
        maxWidth: '1200px',
        margin: '0 auto',
      }}>
        <div style={{
          position: 'relative',
          background: 'rgba(13, 20, 40, 0.85)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(212, 175, 55, 0.12)',
          borderRadius: '24px',
          padding: isMobile ? '28px 20px' : '40px 40px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center',
          gap: isMobile ? '24px' : '48px',
          boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
        }}>
          <style>{`
            @keyframes about-float {
              0%, 100% { transform: translateY(0) rotate(0deg); }
              25% { transform: translateY(-6px) rotate(1deg); }
              75% { transform: translateY(4px) rotate(-1deg); }
            }
            @keyframes about-orbit {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            @keyframes about-orbitReverse {
              from { transform: rotate(360deg); }
              to { transform: rotate(0deg); }
            }
            @keyframes about-pulse {
              0%, 100% { opacity: 0.4; transform: scale(1); }
              50% { opacity: 0.8; transform: scale(1.1); }
            }
            @keyframes about-gradientFlow {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
          `}</style>

          {/* Градиентная рамка */}
          <div style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '24px',
            background: 'linear-gradient(135deg, rgba(212,175,55,0.15), transparent 50%)',
            pointerEvents: 'none',
          }} />

          {/* Цветная полоска сверху */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: '10%',
            right: '10%',
            height: '2px',
            background: 'linear-gradient(90deg, transparent, #d4af37, transparent)',
          }} />

          {/* Анимированная SVG-иллюстрация */}
          <div style={{
            flex: isMobile ? '0 0 auto' : '0 0 240px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'about-float 5s ease-in-out infinite',
            filter: 'drop-shadow(0 0 20px rgba(212, 175, 55, 0.3))',
          }}>
            <svg viewBox="0 0 240 240" width={isMobile ? '160' : '220'} height={isMobile ? '160' : '220'}>
              <defs>
                <radialGradient id="about-glow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#d4af37" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#d4af37" stopOpacity="0" />
                </radialGradient>
                <linearGradient id="about-ring" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#d4af37" stopOpacity="0.6" />
                  <stop offset="50%" stopColor="#7dd3c0" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#9B59B6" stopOpacity="0.6" />
                </linearGradient>
              </defs>
              {/* Фоновое свечение */}
              <circle cx="120" cy="120" r="110" fill="url(#about-glow)" />
              {/* Внешнее кольцо */}
              <g style={{ animation: 'about-orbit 30s linear infinite', transformOrigin: '120px 120px' }}>
                <circle cx="120" cy="120" r="95" fill="none" stroke="url(#about-ring)" strokeWidth="0.8" strokeDasharray="4 8" />
                <circle cx="120" cy="25" r="4" fill="#d4af37" opacity="0.8" />
                <circle cx="215" cy="120" r="3" fill="#7dd3c0" opacity="0.7" />
              </g>
              {/* Среднее кольцо */}
              <g style={{ animation: 'about-orbitReverse 20s linear infinite', transformOrigin: '120px 120px' }}>
                <circle cx="120" cy="120" r="70" fill="none" stroke="#d4af37" strokeWidth="0.5" opacity="0.3" strokeDasharray="2 6" />
                <circle cx="50" cy="120" r="3.5" fill="#9B59B6" opacity="0.7" />
                <circle cx="190" cy="120" r="3" fill="#FF9800" opacity="0.6" />
              </g>
              {/* Внутреннее кольцо */}
              <g style={{ animation: 'about-orbit 15s linear infinite', transformOrigin: '120px 120px' }}>
                <circle cx="120" cy="120" r="45" fill="none" stroke="#7dd3c0" strokeWidth="0.5" opacity="0.25" />
                <circle cx="120" cy="75" r="2.5" fill="#d4af37" opacity="0.6" />
              </g>
              {/* Центр — логотип/ядро */}
              <circle cx="120" cy="120" r="22" fill="rgba(13, 20, 40, 0.8)" stroke="#d4af37" strokeWidth="1.5" />
              <circle cx="120" cy="120" r="26" fill="none" stroke="#d4af37" strokeWidth="0.5" opacity="0.3" style={{ animation: 'about-pulse 3s ease-in-out infinite' }} />
              {/* Звезда в центре */}
              <path d="M120 105 L123 115 L133 115 L125 121 L128 131 L120 125 L112 131 L115 121 L107 115 L117 115Z" fill="#d4af37" opacity="0.9" />
              {/* Звёзды-декор */}
              <circle cx="35" cy="45" r="1.2" fill="#fff" opacity="0.5" />
              <circle cx="200" cy="55" r="1" fill="#fff" opacity="0.4" />
              <circle cx="40" cy="190" r="1.5" fill="#fff" opacity="0.3" />
              <circle cx="195" cy="185" r="1" fill="#fff" opacity="0.4" />
              <circle cx="80" cy="30" r="0.8" fill="#d4af37" opacity="0.5" />
              <circle cx="170" cy="200" r="0.8" fill="#7dd3c0" opacity="0.4" />
            </svg>
          </div>

          {/* Текстовый контент */}
          <div style={{ flex: 1, position: 'relative', zIndex: 1, textAlign: isMobile ? 'center' : 'left' }}>
            <h2 style={{
              fontSize: isMobile ? '24px' : '32px',
              fontFamily: 'var(--font-display)',
              fontStyle: 'italic',
              background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
              backgroundSize: '200% auto',
              animation: 'about-gradientFlow 4s ease infinite',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              margin: '0 0 16px',
              letterSpacing: '0.02em',
            }}>
              О платформе
            </h2>
            <p style={{
              color: '#b0b0b0',
              fontSize: isMobile ? '14px' : '16px',
              lineHeight: 1.8,
              fontFamily: 'var(--font-sans)',
              margin: 0,
            }}>
              <strong style={{ color: '#d4af37' }}>«Астранум»</strong> — это не просто приложение, а результат работы целой исследовательской лаборатории, где вековая мудрость астрологии и нумерологии соединяется с современной вычислительной мощностью. Наша команда профессиональных методологов и data-сайентистов превратила сакральные знания в стройную, математически выверенную систему, исключающую дилетантские догадки. С <strong style={{ color: '#d4af37' }}>«Астранум»</strong> вы получаете не абстрактные предсказания, а персонализированную дорожную карту вашей личности и будущего — анализ, которому действительно можно доверять.
            </p>
          </div>
        </div>
      </div>

      {/* Как это работает */}
      <HowItWorks />

      {/* Каталог предсказаний */}
      <PredictionCarousel />

      {/* Кнопка "Начать бесплатно" */}
      {!isAuthenticated && (
        <div style={{
          textAlign: 'center',
          padding: isMobile ? '24px 16px' : '32px 20px',
        }}>
          <button
            onClick={() => { setAuthMode('register'); setShowAuthModal(true); }}
            style={{
              background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
              border: 'none',
              padding: '18px 56px',
              borderRadius: '30px',
              color: '#0a0a1a',
              fontSize: '20px',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-sans)',
              boxShadow: '0 4px 20px rgba(212, 175, 55, 0.4)',
              transition: 'all 0.3s',
              letterSpacing: '0.5px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 30px rgba(212, 175, 55, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(212, 175, 55, 0.4)';
            }}
          >
            Начать бесплатно
          </button>
        </div>
      )}

      {/* Модальное окно регистрации */}
      {showAuthModal && (
        <AuthModal
          mode={authMode}
          onClose={() => setShowAuthModal(false)}
          onSwitchMode={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}
        />
      )}
    </div>
  );
};

export default HomePage;
